import {
  Archive,
  ArrowsClockwiseIcon,
  CaretDownIcon,
  CaretRightIcon,
  CloudArrowUp,
  DownloadSimple,
  File,
  Trash
} from "@phosphor-icons/react";
import {
  Fragment,
  type ReactNode,
  useEffect,
  useState
} from "react";
import type { R2Binding } from "../../api/r2/model.ts";
import type { R2Object } from "@workerstation/shared/resources/object-queue";
import { useR2Explorer } from "./react.tsx";
import {
  formatResourceBytes,
  ResourceTreeAction,
  ResourceTreeRow
} from "../../ResourceExplorerTree";

interface R2ExplorerProps {
  workspaceId: string | null;
  connectionName: string;
  collapsed: boolean;
  headerLeading?: ReactNode;
  refreshToken?: number;
  onCollapsedChange: (collapsed: boolean) => void;
}

export function R2Explorer({
  workspaceId,
  connectionName,
  collapsed,
  headerLeading,
  refreshToken,
  onCollapsedChange
}: R2ExplorerProps) {
  const { snapshot, dispatch } = useR2Explorer(workspaceId, refreshToken);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(["connection"]));
  const [selectedBinding, setSelectedBinding] = useState<string | null>(null);
  const { loading, mutating, error } = snapshot;
  useEffect(() => { setExpanded(new Set(["connection"])); setSelectedBinding(null); }, [workspaceId, refreshToken]);
  function toggle(key: string) {
    setExpanded(current => { const next = new Set(current); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  }
  const selectedTarget = (binding: R2Binding) => workspaceId === null ? null : { workspaceId, bindingName: binding.bindingName, namespace: binding.namespace };
  function upload(binding: R2Binding) { const target = selectedTarget(binding); if (target) dispatch({ _tag: "Upload", target }); }
  function download(binding: R2Binding, object: R2Object) { const target = selectedTarget(binding); if (target) dispatch({ _tag: "Download", target, key: object.key }); }
  function remove(binding: R2Binding, object: R2Object) { const target = selectedTarget(binding); if (target) dispatch({ _tag: "Delete", target, key: object.key }); }
  const refresh = () => dispatch({ _tag: "Refresh" });

  const selected = snapshot.bindings.find(
    (binding) => binding.bindingName === selectedBinding
  );

  return (
    <section className="flex h-full min-h-0 w-full flex-col border-t border-kumo-line">
      <div className="flex h-7 shrink-0 items-center border-b border-kumo-line/60 px-1">
        {headerLeading ?? (
          <button
            type="button"
            aria-expanded={!collapsed}
            onClick={() => onCollapsedChange(!collapsed)}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 bg-transparent px-1 text-left text-[10px] font-semibold uppercase tracking-wide text-kumo-default"
          >
            {collapsed ? (
              <CaretRightIcon size={10} weight="bold" />
            ) : (
              <CaretDownIcon size={10} weight="bold" />
            )}
            R2
          </button>
        )}
        <ResourceTreeAction
          label="Upload object"
          disabled={!selected || mutating}
          onClick={() => {
            if (selected) upload(selected);
          }}
        >
          <CloudArrowUp size={13} />
        </ResourceTreeAction>
        <ResourceTreeAction label="Refresh R2" onClick={() => void refresh()}>
          <ArrowsClockwiseIcon
            size={13}
            className={loading ? "animate-spin" : ""}
          />
        </ResourceTreeAction>
      </div>

      {!collapsed ? (
        <div role="tree" className="min-h-0 flex-1 overflow-auto py-1">
          {workspaceId ? (
            snapshot.bindings.length ? (
              <>
                <ResourceTreeRow
                  depth={0}
                  label={connectionName || "Workerspace"}
                  detail={`${snapshot.bindings.length} bucket${snapshot.bindings.length === 1 ? "" : "s"}`}
                  icon={<Archive size={14} />}
                  expandable
                  expanded={expanded.has("connection")}
                  onClick={() => toggle("connection")}
                />
                {expanded.has("connection")
                  ? snapshot.bindings.map((binding) => {
                      const bindingKey = `binding:${binding.bindingName}`;
                      return (
                        <Fragment key={binding.bindingName}>
                          <ResourceTreeRow
                            depth={1}
                            label={binding.bindingName}
                            detail={`${binding.namespace} · ${binding.objects.length}${binding.truncated ? "+" : ""}`}
                            icon={<Archive size={14} weight="fill" />}
                            expandable
                            expanded={expanded.has(bindingKey)}
                            onClick={() => {
                              setSelectedBinding(binding.bindingName);
                              toggle(bindingKey);
                            }}
                            actions={
                              <ResourceTreeAction
                                label={`Upload to ${binding.bindingName}`}
                                disabled={mutating}
                                onClick={() => upload(binding)}
                              >
                                <CloudArrowUp size={12} />
                              </ResourceTreeAction>
                            }
                          />
                          {expanded.has(bindingKey)
                            ? binding.objects.map((object) => (
                                <ResourceTreeRow
                                  key={object.key}
                                  depth={2}
                                  label={object.key}
                                  detail={formatResourceBytes(object.size)}
                                  title={`${object.httpMetadata?.contentType ?? "application/octet-stream"} · ${new Date(object.uploaded).toLocaleString()}`}
                                  icon={<File size={13} />}
                                  onDoubleClick={() =>
                                    download(binding, object)
                                  }
                                  actions={
                                    <>
                                      <ResourceTreeAction
                                        label={`Download ${object.key}`}
                                        onClick={() =>
                                          download(binding, object)
                                        }
                                      >
                                        <DownloadSimple size={12} />
                                      </ResourceTreeAction>
                                      <ResourceTreeAction
                                        label={`Delete ${object.key}`}
                                        disabled={mutating}
                                        onClick={() =>
                                          void remove(binding, object)
                                        }
                                      >
                                        <Trash size={12} />
                                      </ResourceTreeAction>
                                    </>
                                  }
                                />
                              ))
                            : null}
                        </Fragment>
                      );
                    })
                  : null}
              </>
            ) : (
              <div className="mx-2 mt-2 rounded border border-dashed border-kumo-line p-2 text-[11px] leading-4 text-kumo-subtle">
                Declare an R2 binding in cloudflare.config.ts, save the
                workerspace, then refresh.
              </div>
            )
          ) : (
            <p className="p-2 text-[11px] text-kumo-subtle">
              Open a workerspace to browse R2.
            </p>
          )}
          {error ? (
            <div className="m-2 rounded bg-red-500/10 p-2 text-[11px] text-status-error">
              {error}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
